/**
 * SKIN-612 — show the disease name next to every ICD-11 code.
 *
 * The SkincAIr app stores two shapes (2026-10-01 production restore): an ICD-11 pick is
 * `[{"icd11-code": code, "label": name}]`, and `lesion_diagnoses` — a multioption whose
 * options are the visit's `suspected_diagnoses` — stores the bare code
 * `[{"multioption": code}]`. A code is named by, in order: the label stored beside it, the
 * label a pick of the same submission stores beside the same code, the API's WHO catalogue
 * (`withCatalogueNames`, fetched by `useIcd11CatalogueNames` for the codes the first two
 * leave unnamed), else nothing — the bare code. Nothing here invents a name or writes one back.
 */

/** The keys a stored entry carries its code under. `multioption` is the bare-code shape. */
const CODE_KEYS = ['icd11-code', 'multioption'] as const;

/** `1F54.1 — Cutaneous leishmaniasis`, or the bare code when nothing names it. */
export function formatDiagnosisCode(code: string, label?: string | null): string {
    return label ? `${code} — ${label}` : code;
}

function parseEntries(value: unknown): unknown[] {
    let parsed = value;
    if (typeof parsed === 'string') {
        const trimmed = parsed.trim();
        if (!trimmed.startsWith('[')) return [];
        try {
            parsed = JSON.parse(trimmed);
        } catch {
            return [];
        }
    }
    return Array.isArray(parsed) ? parsed : [];
}

function entryCode(entry: unknown, keys: ReadonlyArray<string> = CODE_KEYS): { code: string; label: string | null } | null {
    if (!entry || typeof entry !== 'object') return null;
    const record = entry as Record<string, unknown>;
    const key = keys.find(k => typeof record[k] === 'string' && (record[k] as string).trim());
    if (!key) return null;
    const label = typeof record.label === 'string' && record.label.trim() ? record.label.trim() : null;
    return { code: (record[key] as string).trim(), label };
}

/**
 * Every code an ICD-11 pick among `records` names, with the label stored beside it. Only
 * `icd11-code` entries count: a multioption of another field may carry a slug that looks
 * like nothing in particular, and must never lend a name to a code.
 */
export function diagnosisLabelsFromRecords(records: ReadonlyArray<{ value?: unknown } | null | undefined>): Map<string, string> {
    const labels = new Map<string, string>();
    for (const record of records) {
        for (const entry of parseEntries(record?.value)) {
            const coded = entryCode(entry, ['icd11-code']);
            if (coded?.label && !labels.has(coded.code)) labels.set(coded.code, coded.label);
        }
    }
    return labels;
}

/**
 * The codes a stored diagnosis value holds, each as "code — name": its own stored label,
 * else the one `labels` holds for the code, else the bare code.
 */
export function describeDiagnosisValue(value: unknown, labels: ReadonlyMap<string, string>): string[] {
    const out: string[] = [];
    for (const entry of parseEntries(value)) {
        const coded = entryCode(entry);
        if (coded) out.push(formatDiagnosisCode(coded.code, coded.label ?? labels.get(coded.code)));
    }
    return out;
}

/**
 * Read-only options for a `forwardedFrom` multioption (`lesion_diagnoses`): one per code
 * the value holds, labelled "code — name". The view has no live source field to forward
 * from, so without these it rendered each stored code as a bare chip.
 */
export function namedCodeOptions(value: unknown, labels: ReadonlyMap<string, string>): { value: string; label: string }[] {
    const options: { value: string; label: string }[] = [];
    for (const entry of parseEntries(value)) {
        const coded = entryCode(entry);
        if (coded && !options.some(o => o.value === coded.code)) {
            options.push({ value: coded.code, label: formatDiagnosisCode(coded.code, coded.label ?? labels.get(coded.code)) });
        }
    }
    return options;
}

/**
 * Whether a field holds ICD-11 codes: a pick (`icd11-code`), or a field forwarding the codes
 * of one (`lesion_diagnoses`: `forwardedValueKey: 'icd11-code'`). Only these fields' codes are
 * ever sent to the catalogue lookup; another forwarded field holds option slugs, which are
 * record data and stay on the client.
 */
export function isIcd11Field(field: { type?: string; forwardedFrom?: string; forwardedValueKey?: string } | null | undefined): boolean {
    return field?.type === 'icd11-code' || field?.forwardedValueKey === 'icd11-code';
}

/**
 * The codes among the diagnosis `values` that neither their own stored label nor `labels`
 * names, each once: what the catalogue lookup is asked for, and nothing more.
 */
export function unnamedDiagnosisCodes(values: ReadonlyArray<unknown>, labels: ReadonlyMap<string, string>): string[] {
    const codes = new Set<string>();
    for (const value of values) {
        for (const entry of parseEntries(value)) {
            const coded = entryCode(entry);
            if (coded && !coded.label && !labels.has(coded.code)) codes.add(coded.code);
        }
    }
    return Array.from(codes);
}

/** The names a visit stores, then the catalogue's for the codes it leaves unnamed. */
export function withCatalogueNames(labels: ReadonlyMap<string, string>, catalogue: ReadonlyMap<string, string>): Map<string, string> {
    const names = new Map(catalogue);
    labels.forEach((label, code) => names.set(code, label));
    return names;
}

interface FieldLike { id?: number | string; type?: string; forwardedValueKey?: string }
interface RecordLike { surveyField?: { id?: number | string } | null; value?: unknown }
interface SectionLike { fields?: ReadonlyArray<FieldLike>; fieldGroups?: ReadonlyArray<{ fields?: ReadonlyArray<FieldLike> }> }
/** Records are typed loosely: submissions reach the web from several reads, offline copies included. */
export interface SubmissionLike {
    surveyRecords?: ReadonlyArray<unknown>;
    fieldGroups?: ReadonlyArray<{ instances?: ReadonlyArray<{ surveyRecords?: ReadonlyArray<unknown> }> }>;
}
export interface SurveyLike {
    sections?: ReadonlyArray<SectionLike> | Readonly<Record<string, SectionLike>>;
}

/** Every record of a submission, flat then grouped. */
export function submissionRecords(submission: SubmissionLike | null | undefined): RecordLike[] {
    return [
        ...(submission?.surveyRecords || []),
        ...(submission?.fieldGroups || []).flatMap(group => (group.instances || []).flatMap(instance => instance.surveyRecords || [])),
    ] as RecordLike[];
}

/** The values of a submission's ICD-11 fields ({@link isIcd11Field}), flat then grouped. */
export function submissionDiagnosisValues(submission: SubmissionLike | null | undefined, survey: SurveyLike | null | undefined): unknown[] {
    const sections: ReadonlyArray<SectionLike> = Array.isArray(survey?.sections)
        ? survey!.sections as ReadonlyArray<SectionLike>
        : Object.values((survey?.sections || {}) as Record<string, SectionLike>);
    const icd11FieldIds = new Set(
        sections
            .flatMap(section => [...(section.fields || []), ...(section.fieldGroups || []).flatMap(group => group.fields || [])])
            .filter(isIcd11Field)
            .map(field => field.id),
    );
    return submissionRecords(submission).filter(record => icd11FieldIds.has(record?.surveyField?.id)).map(record => record.value);
}
